import { formatDuration, formatNumber } from '../../lib/format';
import type { Call, CallSortField } from '../../types';

type Props = {
  af: string;
  calls: Call[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  sortBy: CallSortField;
  sortOrder: 'asc' | 'desc';
  onSort: (field: CallSortField) => void;
  onPage: (page: number) => void;
  onSelect: (callId: number) => void;
};

function statusOf(c: Call): { className: string; label: string } {
  if (c.voicemail) return { className: 'voicemail', label: 'Voicemail' };
  if (c.ivrDetected) return { className: 'ivr', label: 'IVR Only' };
  if (c.answered) return { className: 'answered', label: 'Answered' };
  return { className: 'missed', label: 'Missed' };
}

function sentimentClass(s: string | null | undefined): string {
  const v = (s ?? '').toLowerCase();
  if (v.includes('positive')) return 'positive';
  if (v.includes('negative')) return 'negative';
  return 'neutral';
}

function tagClass(tag: string): string {
  if (tag === 'Good Lead') return 'good-lead';
  if (tag === 'Schedule booked') return 'booked';
  return '';
}

function formatCallDate(iso: string): string {
  const dt = new Date(iso);
  return (
    dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' +
    dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

const SORTABLE: Array<{ field: CallSortField; label: string }> = [
  { field: 'start_time', label: 'Date' },
  { field: 'customer_name', label: 'Caller' },
  { field: 'duration', label: 'Duration' },
  { field: 'source', label: 'Source' },
];

export default function CallsTable({
  af,
  calls,
  total,
  page,
  perPage,
  totalPages,
  sortBy,
  sortOrder,
  onSort,
  onPage,
  onSelect,
}: Props) {
  const showingFrom = total === 0 ? 0 : (page - 1) * perPage + 1;
  const showingTo = Math.min(page * perPage, total);

  return (
    <>
      <div className="calls-table">
        <table>
          <thead>
            <tr>
              <SortableTh field="start_time" label="Date" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
              <th>Status</th>
              <SortableTh field="customer_name" label="Caller" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
              <SortableTh field="duration" label="Duration" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
              <SortableTh field="source" label="Source" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
              <th>Sentiment</th>
              <th>Tags</th>
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 ? (
              <tr>
                <td colSpan={SORTABLE.length + 3} style={{ textAlign: 'center', padding: 40, color: '#8a95a5' }}>
                  No calls found
                </td>
              </tr>
            ) : (
              calls.map((c) => {
                const s = statusOf(c);
                const tags = c.callrailTags ?? [];
                const srcAf = c.sourceAf ?? c.afNumber;
                return (
                  <tr key={c.id} onClick={() => onSelect(c.id)}>
                    <td>{formatCallDate(c.startTime)}</td>
                    <td>
                      <span className={`call-status-dot ${s.className}`} />
                      {s.label}
                    </td>
                    <td>
                      {c.customerName || 'Unknown'}
                      {srcAf && srcAf !== af && (
                        <span
                          className="call-sourceaf"
                          title={`Call tracked under ${srcAf}`}
                          style={{
                            display: 'inline-block',
                            marginLeft: 6,
                            padding: '1px 6px',
                            background: '#eef2ff',
                            color: '#3730a3',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            verticalAlign: 'middle',
                          }}
                        >
                          {srcAf}
                        </span>
                      )}
                    </td>
                    <td>{formatDuration(c.duration ?? 0)}</td>
                    <td>{c.source || 'Direct'}</td>
                    <td>
                      {c.callrailSentiment ? (
                        <span className={`call-sentiment ${sentimentClass(c.callrailSentiment)}`}>
                          {c.callrailSentiment}
                        </span>
                      ) : (
                        <span style={{ color: '#b0b8c4' }}>-</span>
                      )}
                    </td>
                    <td>
                      {tags.slice(0, 3).map((t, i) => (
                        <span key={i} className={`call-tag ${tagClass(t)}`}>{t}</span>
                      ))}
                      {tags.length > 3 && (
                        <span style={{ fontSize: 11, color: '#8a95a5' }}>+{tags.length - 3}</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="calls-pagination">
        <span>
          Showing {showingFrom}-{showingTo} of {formatNumber(total)} calls
        </span>
        <div>
          <button onClick={() => onPage(page - 1)} disabled={page <= 1}>
            ← Prev
          </button>{' '}
          <button onClick={() => onPage(page + 1)} disabled={page >= totalPages}>
            Next →
          </button>
        </div>
      </div>
    </>
  );
}

function SortableTh({
  field,
  label,
  sortBy,
  sortOrder,
  onSort,
}: {
  field: CallSortField;
  label: string;
  sortBy: CallSortField;
  sortOrder: 'asc' | 'desc';
  onSort: (f: CallSortField) => void;
}) {
  const isSorted = sortBy === field;
  return (
    <th onClick={() => onSort(field)} className={isSorted ? 'sorted' : ''}>
      {label}
      <span className="sort-arrow">
        {isSorted ? (sortOrder === 'desc' ? '▼' : '▲') : '▼'}
      </span>
    </th>
  );
}
